import { escapeRegExp } from '@/utils/string';
import { toQueryString } from '@/utils/url';
import Axios from 'axios';

/**
 * @typedef {Object} MockedResponse
 * @property {?number} status
 * @property {?*} data
 */

export default class Http {
  /**
   * Http constructor.
   */
  constructor() {
    this._axios = Axios.create();
    this._mocks = [];
    this._cache = new Map();
  }

  /**
   * Determine if the error is a cancel error.
   * @param error
   * @returns {boolean}
   */
  static isCancel(error) {
    return Axios.isCancel(error);
  }

  /**
   * Set common headers.
   * @param {Object.<string, string>} headers
   */
  setCommonHeaders(headers) {
    this._axios.defaults.headers.common = headers;
  }

  /**
   * Send a GET request.
   * @param {string} url
   * @param {*} config
   * @returns {AxiosPromise}
   */
  get(url, config = {}) {
    return this._sendRequest('get', url, config);
  }

  /**
   * Send a POST request.
   * @param {string} url
   * @param {*} config
   * @returns {AxiosPromise}
   */
  post(url, config = {}) {
    return this._sendRequest('post', url, config);
  }

  /**
   * Send a PUT request.
   * @param {string} url
   * @param {*} config
   * @returns {AxiosPromise}
   */
  put(url, config = {}) {
    return this._sendRequest('put', url, config);
  }

  /**
   * Send a PATCH request.
   * @param {String} url
   * @param {*} config
   * @returns {AxiosPromise}
   */
  patch(url, config = {}) {
    return this._sendRequest('patch', url, config);
  }

  /**
   * Send a DELETE request.
   * @param {string} url
   * @param {*} config
   * @returns {AxiosPromise}
   */
  delete(url, config = {}) {
    return this._sendRequest('delete', url, config);
  }

  /**
   * Abort request controller.
   * @returns {AbortController}
   */
  abortController() {
    return new AbortController();
  }

  /**
   * Add a request interceptor.
   * @param {function|boolean} request
   * @param {function|boolean} error
   * @returns {number}
   */
  addRequestInterceptor(request, error) {
    return this._axios.interceptors.request.use(request, error);
  }

  /**
   * Add a response and response error interceptor.
   * @param {function|boolean} response
   * @param {function|boolean} error
   * @returns {number}
   */
  addResponseInterceptor(response, error) {
    return this._axios.interceptors.response.use(response, error);
  }

  /**
   * Mock request response.
   * @param {string} url
   * @param {function(*): MockedResponse} response
   * @param {'*'|'get'|'post'} method
   */
  mock(url, response, method = '*') {
    this._mocks.push({
      url: url,
      matcher: escapeRegExp(url).replace(/\\\{(.*?)\\\}/igu, '(?<$1>.*?)'),
      method: method,
      response: response,
    });
  }

  /**
   * Clear specific mock.
   * @param {string} url
   * @param {string} method
   */
  clearMock(url, method = '*') {
    this._mocks.filter(mock => mock.url !== url && mock.method !== method);
  }

  /**
   * Clear mocks.
   */
  clearMocks() {
    this._mocks = [];
  }

  /**
   * Execute http request.
   * @param {string} method
   * @param {string} url
   * @param {undefined | *} config
   * @returns {AxiosPromise | *}
   * @private
   */
  _sendRequest(method, url, config) {
    let fullUrl = url;
    if (config.params) {
      fullUrl += `?${toQueryString(config.params)}`;
    }

    // Prevent duplicate requests when one is still resolving
    if (this._cache.has(fullUrl)) {
      return this._cache.get(fullUrl).promise;
    }

    const abortController = this.abortController();

    const promise = this._axios({
      method: method,
      url: url,
      signal: abortController.signal,
      ...config,
    });

    // add abort function to promise for cancelling request when needed
    promise.abort = {
      abort() {
        return abortController.abort();
      },
    };

    // Clear cache on resolve
    this._cache.delete(fullUrl);

    return promise;
  }
}
